import { NumInput } from "./NumberInput";

const fmtAmt = (v) =>
  (parseFloat(v) || 0).toLocaleString("en-IN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
const fmtWt = (v) =>
  (parseFloat(v) || 0).toLocaleString("en-IN", {
    minimumFractionDigits: 3,
    maximumFractionDigits: 3,
  });
const n = (v) => parseFloat(v) || 0;

/**
 * TxBar
 * -----
 * The read-only summary bar shown for each saved transaction.
 * Appearance is always the same; content cells are driven by `cells` prop.
 *
 * Props:
 *   index       {number}   - 0-based position (shown as #1, #2 …)
 *   label       {string}   - optional label shown next to #N in the header (e.g. brand name)
 *   paidAmount  {string}   - current paid value for this transaction
 *   onPaidChange{function} - called with new paid string
 *   txId        {string}   - used for input id namespacing
 *   prefix      {string}   - used for input id namespacing
 *   onEdit      {function} - called with txId
 *   onRemove    {function} - called with txId
 *
 *   cells       {Array<{ label: string, value: string, unit?: string }>}
 *               - the data cells to show BEFORE the Paid cell.
 *               - each section passes its own relevant fields.
 *               - e.g. PC passes: [{ label: "Weight", value: "233.280", unit: "g" },
 *                                  { label: "Charges", value: "1200.00", unit: "Rs" }]
 *               - the Paid + Remaining cells are always appended automatically.
 *
 *   totalAmount {string|number} - used to compute Remaining (section passes tx.totalAmount)
 */
export function TxBar({
  index,
  label,
  paidAmount,
  onPaidChange,
  txId,
  prefix,
  onEdit,
  onRemove,
  cells = [],
  totalAmount,
}) {
  const total = n(totalAmount);
  const paid = n(paidAmount);
  const remaining = total - paid;

  const remainColor =
    total === 0
      ? "#ffb703"
      : remaining === 0
        ? "#4caf50"
        : remaining < 0
          ? "#ffff"
          : paid > 0
            ? "#ffb703"
            : "#ff6b6b";

  const statusText =
    total === 0
      ? "—"
      : remaining === 0
        ? "✓ Paid"
        : remaining < 0
          ? "Overpaid"
          : paid > 0
            ? "Partial"
            : "Unpaid";

  // total columns = custom cells + Paid + Remaining
  const colCount = cells.length + 2;

  return (
    <div
      style={{
        marginBottom: 8,
        borderRadius: 5,
        overflow: "hidden",
        border: "1px solid #c8960a",
      }}
    >
      {/* ── header strip ── */}
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          padding: "4px 10px",
          background: "#5c3f00",
          borderBottom: "1px solid #c8960a",
        }}
      >
        <span style={{ fontSize: 12, fontWeight: 700, color: "#ffb703" }}>
          #{index + 1}
          {label && (
            <span style={{ color: "#fff", fontWeight: 400, marginLeft: 5 }}>
              · {label}
            </span>
          )}
        </span>
        <div style={{ display: "flex", gap: 6 }}>
          <button
            onClick={() => onEdit(txId)}
            style={{
              padding: "2px 12px",
              fontSize: 11,
              fontWeight: 600,
              borderRadius: 3,
              border: "1px solid #ffb703",
              background: "#575656",
              color: "#ffb703",
              cursor: "pointer",
            }}
          >
            Edit
          </button>
          <button
            onClick={() => onRemove(txId)}
            style={{
              padding: "2px 12px",
              fontSize: 11,
              fontWeight: 600,
              borderRadius: 3,
              border: "1px solid #ff6b6b",
              background: "#575656",
              color: "#ff6b6b",
              cursor: "pointer",
            }}
          >
            Remove
          </button>
        </div>
      </div>

      {/* ── data cells ── */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: `repeat(${colCount}, 1fr)`,
          background: "#7a5600",
        }}
      >
        {/* custom cells */}
        {cells.map((cell, i) => (
          <div
            key={i}
            style={{
              padding: "7px 6px",
              textAlign: "center",
              borderRight: "1px solid #c8960a",
            }}
          >
            <div
              style={{
                fontSize: 10,
                color: "#ffb703",
                fontWeight: 600,
                textTransform: "uppercase",
                letterSpacing: "0.3px",
                marginBottom: 2,
              }}
            >
              {cell.label}
            </div>
            <div style={{ fontSize: 13, fontWeight: 700, color: "#fff" }}>
              {cell.unit === "Rs" && (
                <span
                  style={{ fontSize: 10, color: "#ffb703", marginRight: 1 }}
                >
                  Rs
                </span>
              )}
              {cell.unit === "Rs" ? fmtAmt(cell.value) : fmtWt(cell.value)}
              {cell.unit && cell.unit !== "Rs" && (
                <span style={{ fontSize: 10, color: "#ffb703", marginLeft: 2 }}>
                  {cell.unit}
                </span>
              )}
            </div>
          </div>
        ))}

        {/* Paid — always editable input */}
        <div
          style={{
            padding: "5px 6px",
            textAlign: "center",
            borderRight: "1px solid #c8960a",
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
          }}
        >
          <div
            style={{
              fontSize: 10,
              color: "#ffb703",
              fontWeight: 600,
              textTransform: "uppercase",
              letterSpacing: "0.3px",
              marginBottom: 3,
            }}
          >
            Paid (Rs)
          </div>
          <NumInput
            id={`${prefix}TxPaid_${txId}`}
            value={paidAmount}
            onChange={onPaidChange}
            placeholder="0.00"
            disabled={total === 0}
            style={{
              padding: "2px 4px",
              fontSize: 12,
              fontWeight: 700,
              color: "#fff",
              background: "#575656",
              border: "1px solid #c8960a",
              borderRadius: 3,
              textAlign: "center",
              width: "100%",
              outline: "none",
            }}
          />
        </div>

        {/* Remaining / Status */}
        <div style={{ padding: "7px 6px", textAlign: "center" }}>
          <div
            style={{
              fontSize: 10,
              color: "#ffb703",
              fontWeight: 600,
              textTransform: "uppercase",
              letterSpacing: "0.3px",
              marginBottom: 2,
            }}
          >
            {remaining === 0 ? "Status" : "Remaining"}
          </div>
          <div style={{ fontSize: 13, fontWeight: 700, color: remainColor }}>
            {remaining <= 0 ? (
              statusText
            ) : (
              <>
                <span
                  style={{ fontSize: 10, color: "#ffb703", marginRight: 1 }}
                >
                  Rs
                </span>
                {fmtAmt(remaining)}
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

/**
 * TxShell
 * -------
 * The outer wrapper every section uses. Handles:
 *   - Heading with transaction count
 *   - Rendering saved transaction bars (via renderBar)
 *   - Showing the form (new or edit) via renderForm
 *   - "Add Another Transaction" dashed button
 *
 * Props:
 *   title         {string}   - section heading text e.g. "PC", "LAB"
 *   transactions  {array}    - from useTxList
 *   editingId     {string}   - from useTxList
 *   showingNewForm{boolean}  - from useTxList
 *   formState     {string}   - from useTxList
 *   openNew       {function} - from useTxList
 *   cancelNew     {function} - from useTxList (used internally in renderForm)
 *
 *   renderBar(tx, index)
 *     - called for each saved tx that is NOT being edited
 *     - section provides: <TxBar ... cells={...} />
 *
 *   renderForm(tx, isEdit, onSave, onCancel)
 *     - called when form is visible (new or edit)
 *     - section renders its own fields and calls onSave(formData) / onCancel()
 */
export function TxShell({
  title,
  transactions,
  editingId,
  showingNewForm,
  formState,
  openNew,
  cancelNew,
  emptyTx,
  renderBar,
  renderForm,
  saveTx,
  cancelEdit,
}) {
  return (
    <>
      {/* ── Heading ── */}
      <h3 className="head">
        {title}
        {transactions.length > 0 ? ` (${transactions.length})` : ""}
      </h3>

      {/* ── Saved transactions: bar or inline edit form ── */}
      {transactions.map((tx, i) =>
        editingId === tx.id ? (
          <div key={tx.id} style={{ marginBottom: 8 }}>
            {renderForm(tx, true, saveTx, cancelEdit)}
          </div>
        ) : (
          <div key={tx.id}>{renderBar(tx, i)}</div>
        ),
      )}

      {/* ── New transaction form (default on load) ── */}
      {showingNewForm &&
        renderForm(
          emptyTx(),
          false,
          saveTx,
          transactions.length > 0 ? cancelNew : undefined,
        )}

      {/* ── Add Another button (only when form is hidden) ── */}
      {formState === "hidden" && (
        <button
          onClick={openNew}
          style={{
            width: "100%",
            padding: "7px 0",
            marginTop: 4,
            background: "transparent",
            border: "1.5px dashed #c8960a",
            borderRadius: 5,
            color: "#ffb703",
            fontSize: 13,
            fontWeight: 600,
            cursor: "pointer",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: 6,
          }}
          onMouseEnter={(e) => (e.currentTarget.style.background = "#5c3f00")}
          onMouseLeave={(e) =>
            (e.currentTarget.style.background = "transparent")
          }
        >
          <span style={{ fontSize: 15, lineHeight: 1 }}>+</span> Add Another
          Transaction
        </button>
      )}
    </>
  );
}
